import React from "react";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type {
  StrategyRdConcentration,
  StrategyRdCostStress,
  StrategyRdHalves,
  StrategyRdHoldoutSplit,
  StrategyRdMonthBucket,
  StrategyRdStability,
} from "../../../types/strategyRd";
import { formatStrategyRdPct, formatStrategyRdRMultiple } from "../../../utils/strategyRd";
import {
  PNL_NEGATIVE_COLOR,
  PNL_POSITIVE_COLOR,
} from "../Statistics/components/statsSignedPnlBarUtils";
import {
  StrategyRdHoldoutVerdictChip,
  strategyRdShortDate,
  StrategyRdVerdictChip,
  strategyRdHeadlineBrief,
  type StrategyRdVerdictTone,
} from "./StrategyRdHoldoutRow";
import { strategyRdMetaClassName } from "./strategyRdCardShell";
import {
  StrategyRdBadge,
  StrategyRdFacts,
  StrategyRdInfo,
  StrategyRdLabCard,
  StrategyRdSubsection,
  strategyRdBodyTextClassName,
  strategyRdCaptionClassName,
  strategyRdFigureClassName,
} from "./strategyRdLabUi";

const GREEN = "#16a34a";
const AMBER = "#64748B";
const RED = "#dc2626";
const GREY = "#6b7280";

export const HALVES: Record<string, StrategyRdVerdictTone> = {
  both_worked: {
    label: "Both halves",
    color: GREEN,
    tip: "First and second half of the locked list both had positive expectancy.",
  },
  first_only: {
    label: "First half only",
    color: AMBER,
    tip: "Only the first half made money. The later trades did not.",
  },
  second_only: {
    label: "Second half only",
    color: AMBER,
    tip: "Only the second half made money. The earlier trades did not.",
  },
  neither: { label: "Neither half", color: RED, tip: "Neither half of the locked list made money." },
  too_few_trades: {
    label: "Too few trades",
    color: GREY,
    tip: "Fewer than 5 trades in a half, not enough to judge.",
  },
};

export const CONCENTRATION: Record<string, StrategyRdVerdictTone> = {
  diversified: {
    label: "Diversified",
    color: GREEN,
    tip: "Expectancy stays positive after dropping the best few trades, and at least half of the full figure.",
  },
  concentrated: {
    label: "Concentrated",
    color: AMBER,
    tip: "Most of the edge is in a handful of trades. Dropping them wipes or halves expectancy.",
  },
  too_few_trades: {
    label: "Too few trades",
    color: GREY,
    tip: "Not enough closed trades to judge concentration.",
  },
};

const COST: Record<string, StrategyRdVerdictTone> = {
  ok: {
    label: "Costs ok",
    color: GREEN,
    tip: "The return stayed positive at double costs and at the instrument's typical costs.",
  },
  fragile_to_costs: {
    label: "Fragile to costs",
    color: AMBER,
    tip: "Double or typical costs turned a positive return negative. Judge by this, not the headline.",
  },
};

const COST_LABELS: Record<string, string> = {
  half_cost: "Half cost",
  double_cost: "Double cost",
  assumed_costs: "Typical costs",
};

function sharePct(v: number | null | undefined): string | null {
  if (v == null || !Number.isFinite(v)) return null;
  return `${Math.round(v * 100)}%`;
}

/** Over 100% the best trades made more than the whole result — a percent stops meaning anything. */
function bestShareSentence(v: number | null | undefined): string | null {
  if (v == null || !Number.isFinite(v)) return null;
  if (v > 1) return "Those trades made more than the whole result. The rest lose money.";
  return `Those trades made ${sharePct(v)} of the total R.`;
}

export function StrategyRdHalvesRow({
  halves,
  compact = false,
}: {
  halves: StrategyRdHalves | null | undefined;
  compact?: boolean;
}): JSX.Element | null {
  if (!halves) return null;
  return (
    <div
      className={cn("flex flex-wrap items-center gap-x-3 gap-y-1", compact ? "text-[11px]" : "text-xs")}
      data-strategy-rd-halves=""
      title="Same locked trades, split at the midpoint of setup time. Not a new scan."
    >
      <span className={strategyRdMetaClassName}>
        First half <span className="text-foreground">{strategyRdHeadlineBrief(halves.first)}</span>
      </span>
      <span className={strategyRdMetaClassName}>
        Second half <span className="text-foreground">{strategyRdHeadlineBrief(halves.second)}</span>
      </span>
      <StrategyRdVerdictChip verdict={halves.verdict} map={HALVES} attr="data-strategy-rd-halves-verdict" />
    </div>
  );
}

export function StrategyRdConcentrationLine({
  concentration,
  compact = false,
}: {
  concentration: StrategyRdConcentration | null | undefined;
  compact?: boolean;
}): JSX.Element | null {
  if (!concentration) return null;
  const dropped = concentration.dropped;
  const without = formatStrategyRdRMultiple(concentration.expectancy_r_without_best);
  const shareRaw = concentration.best_share_of_sum_r;
  const share = shareRaw != null && shareRaw > 1 ? "all" : sharePct(shareRaw);
  const bits = [
    dropped != null
      ? `Without the best ${dropped} trade${dropped === 1 ? "" : "s"}`
      : "Without the best trades",
    share ? `(${share} of total R${share === "all" ? " and more" : ""})` : null,
    without ? `${without} / trade` : null,
  ].filter(Boolean);
  return (
    <div
      className={cn("flex flex-wrap items-center gap-x-3 gap-y-1", compact ? "text-[11px]" : "text-xs")}
      data-strategy-rd-concentration=""
    >
      <span className={strategyRdMetaClassName}>
        <span className="text-foreground">{bits.join(" ")}</span>
      </span>
      <StrategyRdVerdictChip
        verdict={concentration.verdict}
        map={CONCENTRATION}
        attr="data-strategy-rd-concentration-verdict"
      />
    </div>
  );
}

function monthLabel(isoMonth: string): string {
  const [y, m] = isoMonth.split("-");
  const month = Number(m);
  if (!y || !Number.isFinite(month) || month < 1 || month > 12) return isoMonth;
  const name = new Date(Date.UTC(Number(y), month - 1, 1)).toLocaleString(undefined, {
    month: "short",
    timeZone: "UTC",
  });
  return `${name} ${y.slice(2)}`;
}

/** Calendar-month expectancy on the locked list — quiet path, not a second backtest. */
export function StrategyRdMonthsPath({
  months,
}: {
  months: StrategyRdMonthBucket[] | null | undefined;
}): JSX.Element | null {
  if (!months || months.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5" data-strategy-rd-months="">
      <p className={cn(strategyRdMetaClassName, "text-xs")}>Monthly path</p>
      <div className="flex flex-wrap gap-1.5">
        {months.map((bucket) => {
          const e = bucket.expectancy_r;
          const color =
            e == null ? GREY : e > 0 ? PNL_POSITIVE_COLOR : e < 0 ? PNL_NEGATIVE_COLOR : GREY;
          const r = formatStrategyRdRMultiple(e);
          return (
            <span
              key={bucket.month}
              title={`${bucket.month}: ${r ?? "N/A"} · ${bucket.closed_count ?? "N/A"} trades`}
              className="inline-flex min-w-[3.25rem] flex-col rounded-[6px] border border-border px-1.5 py-1"
              data-strategy-rd-month={bucket.month}
            >
              <span className={cn(strategyRdMetaClassName, "text-[10px] leading-none")}>
                {monthLabel(bucket.month)}
              </span>
              <span className="mt-1 text-[11px] font-medium tabular-nums" style={{ color }}>
                {r ?? "N/A"}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

const NA = "N/A";

type SplitPart = {
  label: string;
  r: number | null | undefined;
  trades: number | null | undefined;
  from?: string | null;
  to?: string | null;
  /** The part the verdict is about (outlined). */
  focus?: boolean;
};

function rColor(v: number | null | undefined): string | undefined {
  return v == null ? undefined : v > 0 ? PNL_POSITIVE_COLOR : v < 0 ? PNL_NEGATIVE_COLOR : undefined;
}

function tradesText(n: number | null | undefined): string {
  return n == null ? NA : `${n} trade${n === 1 ? "" : "s"}`;
}

function dateRange(from: string | null | undefined, to: string | null | undefined): string | null {
  const a = strategyRdShortDate(from);
  const b = strategyRdShortDate(to);
  return a && b ? `${a} to ${b}` : null;
}

/**
 * The period as the check splits it: one thin segment per part, as wide as its trade count,
 * tinted by whether that part made money. The focus part is the one the verdict is about.
 */
function SplitBar({ parts }: { parts: SplitPart[] }): JSX.Element {
  const total = parts.reduce((a, p) => a + Math.max(1, p.trades ?? 0), 0);
  return (
    <div className="flex h-2 w-full gap-1" aria-hidden data-strategy-rd-split-bar="">
      {parts.map((p) => {
        const r = p.r ?? null;
        const tint = r == null ? "var(--muted-foreground)" : r > 0 ? PNL_POSITIVE_COLOR : r < 0 ? PNL_NEGATIVE_COLOR : "var(--muted-foreground)";
        return (
          <span
            key={p.label}
            className="h-full rounded-[2px]"
            style={{
              flexGrow: Math.max(1, p.trades ?? 0) / total,
              flexBasis: 0,
              background: tint,
              opacity: p.focus ? 0.9 : 0.35,
            }}
          />
        );
      })}
    </div>
  );
}

/** Part, trades and R per trade for each piece of the split, as a small table. */
function SplitTable({ parts, attr }: { parts: SplitPart[]; attr?: string }): JSX.Element {
  const head = cn(strategyRdCaptionClassName, "h-8 px-3 text-[11px]");
  // The app's table look: bordered, muted header row, padded cells.
  return (
    <div className="overflow-hidden !rounded-[6px] !border !border-solid !border-border">
    <Table {...(attr ? { [attr]: "" } : {})}>
      <TableHeader>
        <TableRow className="bg-muted/50 hover:bg-muted/50">
          <TableHead className={head}>Part</TableHead>
          <TableHead className={cn(head, "text-right")}>Trades</TableHead>
          <TableHead className={cn(head, "text-right")}>R / trade</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {parts.map((p) => {
          const dates = dateRange(p.from, p.to);
          return (
            <TableRow key={p.label} className="hover:bg-transparent">
              <TableCell className="px-3 py-2 align-top">
                <span className="flex flex-col">
                  <span
                    className={cn(
                      "font-[family-name:var(--font-primary)] text-xs",
                      p.focus
                        ? "font-medium text-gray-600 dark:text-white"
                        : "text-gray-500 dark:text-sidebar-foreground"
                    )}
                  >
                    {p.label}
                  </span>
                  {dates ? (
                    <span className="font-[family-name:var(--font-primary)] text-[11px] text-muted-foreground">
                      {dates}
                    </span>
                  ) : null}
                </span>
              </TableCell>
              <TableCell className="px-3 py-2 text-right align-top font-ibm-plex text-xs tabular-nums text-gray-500 dark:text-sidebar-foreground">
                {p.trades ?? NA}
              </TableCell>
              <TableCell
                className="px-3 py-2 text-right align-top font-ibm-plex text-xs font-medium tabular-nums text-gray-600 dark:text-white"
                style={{ color: rColor(p.r) }}
              >
                {formatStrategyRdRMultiple(p.r) ?? NA}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    </div>
  );
}

/** The figure a check is judged on: R per trade, signed colour. */
function CheckFigure({ value, unit }: { value: number | null | undefined; unit: string }): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5">
      <span className={strategyRdFigureClassName} style={{ color: rColor(value) }}>
        {formatStrategyRdRMultiple(value) ?? NA}
      </span>
      <span className={strategyRdBodyTextClassName}>{unit}</span>
    </div>
  );
}

const PASSED = new Set(["held_up", "both_worked", "diversified"]);
const UNJUDGED = new Set(["too_few_trades"]);

/**
 * Overview: the same finished trades split three ways (unseen period, halves, without the
 * best trades). A real edge survives all of them. Each check is its own block: headline,
 * verdict badge, the figure it is judged on, then the parts behind it.
 */
export function StrategyRdStabilitySection({
  split,
  stability,
}: {
  split: StrategyRdHoldoutSplit | null | undefined;
  stability: StrategyRdStability | null | undefined;
}): JSX.Element | null {
  const halves = stability?.halves ?? null;
  const conc = stability?.concentration ?? null;
  const months = stability?.months ?? [];
  if (!split && !halves && !conc && months.length === 0) return null;
  const pct = split?.holdout_pct != null ? Math.round(split.holdout_pct) : null;
  const count = [split, halves, conc].filter(Boolean).length;

  const judged = [split?.verdict, halves?.verdict, conc?.verdict].filter(
    (v): v is string => Boolean(v) && !UNJUDGED.has(v as string)
  );
  const passed = judged.filter((v) => PASSED.has(v)).length;
  const summary =
    judged.length > 0 ? (
      <StrategyRdBadge
        tone={passed === judged.length ? "good" : passed === 0 ? "bad" : "warn"}
        title="Checks with enough trades to judge"
        data-strategy-rd-stability-summary={`${passed}/${judged.length}`}
      >
        {passed} of {judged.length} checks passed
      </StrategyRdBadge>
    ) : null;

  const dropped = conc?.dropped ?? null;
  const shareRaw = conc?.best_share_of_sum_r;

  return (
    <StrategyRdLabCard
      title="Is the edge real?"
      description="The same trades, split three ways. A real edge survives all of them, so judge by these rather than the headline return."
      actions={summary}
      data-strategy-rd-stability=""
    >
      {count > 0 ? (
        <div className="@container/checks">
          <div
            className={cn(
              "grid grid-cols-1 gap-4",
              count === 3 && "@[52rem]/checks:grid-cols-3",
              count === 2 && "@[34rem]/checks:grid-cols-2"
            )}
          >
            {split ? (
              <StrategyRdSubsection
                title={pct != null ? `Unseen data · last ${pct}%` : "Unseen data"}
                badge={<StrategyRdHoldoutVerdictChip verdict={split.verdict} />}
                info={
                  <StrategyRdInfo title="Unseen period">
                    <p>
                      The last {pct ?? 30}% of the backtest is held back as a fresh test: the strategy is shaped on the
                      earlier <strong>tuning period</strong>, then checked on dates it was never tuned on.
                    </p>
                    <p>
                      If the edge is real it keeps working here. A strategy that only shines in the tuning period was
                      fitted to the past, and it won't repeat.
                    </p>
                  </StrategyRdInfo>
                }
                data-strategy-rd-holdout=""
              >
                <CheckFigure value={split.unseen?.expectancy_r} unit="per trade, unseen" />
                <SplitBar
                  parts={[
                    { label: "Tuning", r: split.tuning?.expectancy_r, trades: split.tuning?.closed_count },
                    { label: "Unseen", r: split.unseen?.expectancy_r, trades: split.unseen?.closed_count, focus: true },
                  ]}
                />
                <SplitTable
                  parts={[
                    { label: "Tuning", r: split.tuning?.expectancy_r, trades: split.tuning?.closed_count, from: split.first_time, to: split.cut_time },
                    { label: "Unseen", r: split.unseen?.expectancy_r, trades: split.unseen?.closed_count, from: split.cut_time, to: split.last_time, focus: true },
                  ]}
                />
              </StrategyRdSubsection>
            ) : null}

            {halves ? (
              <StrategyRdSubsection
                title="First vs second half"
                badge={<StrategyRdVerdictChip verdict={halves.verdict} map={HALVES} attr="data-strategy-rd-halves-verdict" />}
                info={
                  <StrategyRdInfo title="First half vs second half">
                    <p>
                      The same trades split in two at the middle of the period
                      {strategyRdShortDate(halves.cut_time) ? ` (${strategyRdShortDate(halves.cut_time)})` : ""}.
                    </p>
                    <p>
                      A dependable edge makes money in <strong>both</strong> halves. If only one half carries all the
                      profit, the result leans on one market phase (a trend, a volatile stretch) that may not come back.
                    </p>
                  </StrategyRdInfo>
                }
                data-strategy-rd-halves=""
              >
                <CheckFigure value={halves.second?.expectancy_r} unit="per trade, second half" />
                <SplitBar
                  parts={[
                    { label: "First half", r: halves.first?.expectancy_r, trades: halves.first?.closed_count },
                    { label: "Second half", r: halves.second?.expectancy_r, trades: halves.second?.closed_count, focus: true },
                  ]}
                />
                <SplitTable
                  parts={[
                    { label: "First half", r: halves.first?.expectancy_r, trades: halves.first?.closed_count, from: split?.first_time, to: halves.cut_time },
                    { label: "Second half", r: halves.second?.expectancy_r, trades: halves.second?.closed_count, from: halves.cut_time, to: split?.last_time, focus: true },
                  ]}
                />
              </StrategyRdSubsection>
            ) : null}

            {conc ? (
              <StrategyRdSubsection
                title={dropped != null ? `Without the best ${dropped}` : "Without the best trades"}
                badge={
                  <StrategyRdVerdictChip
                    verdict={conc.verdict}
                    map={CONCENTRATION}
                    attr="data-strategy-rd-concentration-verdict"
                  />
                }
                info={
                  <StrategyRdInfo title="Without the best trades">
                    <p>
                      The result again with the few biggest winners taken out. Lucky outliers (one news spike, one huge
                      trend day) can make a weak strategy look great.
                    </p>
                    <p>
                      If the edge is still positive without them, profits come from the rules repeating, not from a
                      handful of lucky trades. <strong>Concentrated</strong> means the result depends on those few.
                    </p>
                  </StrategyRdInfo>
                }
                data-strategy-rd-concentration=""
              >
                <CheckFigure value={conc.expectancy_r_without_best} unit="per trade, without them" />
                <SplitBar
                  parts={[
                    { label: "Rest", r: conc.expectancy_r_without_best, trades: conc.closed_count != null && dropped != null ? conc.closed_count - dropped : conc.closed_count, focus: true },
                    // The dropped trades are the biggest winners: always drawn as profit.
                    { label: "Best", r: 1, trades: dropped },
                  ]}
                />
                <SplitTable
                  attr="data-strategy-rd-without-best-bars"
                  parts={[
                    { label: "All trades", r: conc.expectancy_r, trades: conc.closed_count },
                    {
                      label: dropped != null ? `Without the best ${dropped}` : "Without the best",
                      r: conc.expectancy_r_without_best,
                      trades: conc.closed_count != null && dropped != null ? conc.closed_count - dropped : null,
                      focus: true,
                    },
                  ]}
                />
                {shareRaw != null && Number.isFinite(shareRaw) ? (
                  <StrategyRdFacts
                    rows={[
                      {
                        key: "share",
                        label: dropped != null ? `Share of total R from the best ${dropped}` : "Share of total R from the best",
                        value: shareRaw > 1 ? "Over 100%" : sharePct(shareRaw) ?? NA,
                      },
                    ]}
                  />
                ) : null}
              </StrategyRdSubsection>
            ) : null}
          </div>
        </div>
      ) : null}
    </StrategyRdLabCard>
  );
}

/** Chat card: halves + concentration (the card does not carry months). */
export function StrategyRdCardStability({
  stability,
}: {
  stability: Pick<StrategyRdStability, "halves" | "concentration"> | null | undefined;
}): JSX.Element | null {
  if (!stability?.halves && !stability?.concentration) return null;
  return (
    <div className="flex flex-col gap-1" data-strategy-rd-card-stability="">
      <StrategyRdHalvesRow halves={stability.halves} compact />
      <StrategyRdConcentrationLine concentration={stability.concentration} compact />
    </div>
  );
}

export function StrategyRdCostStressChip({
  verdict,
}: {
  verdict: string | null | undefined;
}): JSX.Element | null {
  return <StrategyRdVerdictChip verdict={verdict} map={COST} attr="data-strategy-rd-cost-stress" />;
}

/** Robustness panel: cost-stress verdict and variant returns. */
export function StrategyRdCostStressBlock({
  costStress,
  baselineReturnPct,
}: {
  costStress: StrategyRdCostStress | null | undefined;
  baselineReturnPct?: number | null;
}): JSX.Element | null {
  if (!costStress) return null;
  return (
    <div className="flex flex-col gap-1.5" data-strategy-rd-cost-stress="">
      <div className="flex flex-wrap items-center gap-2">
        <StrategyRdCostStressChip verdict={costStress.verdict} />
        <p className={cn(strategyRdMetaClassName, "text-xs")}>
          Same trades, re-priced at half, double and the instrument's typical costs.
        </p>
      </div>
      {costStress.variants.length > 0 ? (
        <ul className={cn(strategyRdMetaClassName, "list-disc space-y-0.5 pl-4")}>
          {baselineReturnPct != null ? (
            <li>This backtest {formatStrategyRdPct(baselineReturnPct) ?? "N/A"}.</li>
          ) : null}
          {costStress.variants.map((v) => (
            <li key={v.label} data-strategy-rd-cost-variant={v.label}>
              {COST_LABELS[v.label] ?? v.label.replace(/_/g, " ")}{" "}
              {formatStrategyRdPct(v.return_pct) ?? "N/A"}
              {v.trades != null ? ` · ${v.trades} trades` : ""}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
